import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks';
import { saveIntendedDestination } from '../utils/redirects';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute: Route guard that redirects unauthenticated users to /login.
 * Saves the intended destination in sessionStorage so it can be recovered after login.
 *
 * Note: isLoading is already handled at the App level, so we don't need to check it here.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, admin } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    saveIntendedDestination(location.pathname, location.search);
    return <Navigate to="/login" replace />;
  }

  // Handle Onboarding logic
  // If not onboarded and trying to access anything other than /onboarding, redirect to onboarding
  if (admin?.shop?.is_onboarded === false && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // If already onboarded and trying to access /onboarding, redirect to dashboard
  if (admin?.shop?.is_onboarded !== false && location.pathname === '/onboarding') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
